import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSettings } from '../mall_settings/SettingsProvider';
import { useSession } from '../session/SessionProvider';

import { globalProperties, getProductDetails, setPreviousPage, getPreviousPage } from './segmentUtils';

export const SegmentContext = React.createContext();

export const PRODUCT_VIEWED_EVENT = 'Product Viewed';
export const PRODUCT_CLICKED_EVENT = 'Product Clicked';
export const PRODUCT_ADDED_EVENT = 'Product Added';
export const PRODUCT_REMOVED_EVENT = 'Product Removed';
export const CHECKOUT_STARTED_EVENT = 'Checkout Started';
export const ORDER_COMPLETE_EVENT = 'Order Completed';
export const CART_VIEWED_EVENT = 'Cart Viewed';

export const SegmentProvider = ({ children }) => {
    const { phoebe, mallCode, segmentEnabled } = useSettings();
    const { cartId, previousSession } = useSession();
    const baseProperties = {
        buyFlow: 'Mallfront',
        productCode: 'MALL',
        mallCode,
        billableProductFeatureCode: 'MALL',
        cashStarSessionId: cartId,
        previousSessionId: previousSession,
    };

    const sendEvents = phoebe && segmentEnabled;

    const sendPageEvent = (location) => {
        setPreviousPage();
        const globalProp = globalProperties(location)
        
        window.analytics && window.analytics.page(globalProp.pageName, {
            channel: globalProp.section,
            ...globalProp
        })
    }

    const sendTrackEvent = (event, data) => {
        const dataProperties = data.properties || {};
        data.properties = { ...baseProperties, ...dataProperties };
        
        window.analytics && window.analytics.track(event, data, { context: { ip: '0.0.0.0' } });
    };

    const sendProductEvent = (eventType, { product, faceplate, amount, quantity }) => {
        const eventData = {
            ...globalProperties(),
            event_name: eventType,
            cart_id: cartId,
            name: product.name,
            brand: product.brandName,
            product_id: product.universalProductCode,
            sku: faceplate.code,
            price: amount,
            quantity,
            value: amount,
            variant: faceplate.code,
            category: product.deliveryMethod,
            image_url: faceplate.imageURL,
        };
        if (eventType === PRODUCT_VIEWED_EVENT) {
            eventData.previousPageName = getPreviousPage();
        }
        sendTrackEvent(eventType, eventData);
    };

    const productClickedEvent = (item) => {
        sendProductEvent(PRODUCT_CLICKED_EVENT, item);
    };

    const productViewedEvent = (item) => {
        sendProductEvent(PRODUCT_VIEWED_EVENT, item);
    };

    const productAddedEvent = (item) => {
        sendProductEvent(PRODUCT_ADDED_EVENT, item);
    };

    const productRemovedEvent = (item) => {
        sendProductEvent(PRODUCT_REMOVED_EVENT, item);
    };

    const cartViewedEvent = (lineItems) => {
        const eventData = {
            ...globalProperties(),
            event_name: CART_VIEWED_EVENT,
            cart_id: cartId,
            products: getProductDetails(lineItems),
        };
        sendTrackEvent(CART_VIEWED_EVENT, eventData);
    };

    const checkoutStartedEvent = (total, shipping, currency, lineItems) => {
        const eventData = {
            ...globalProperties(),
            event_name: CHECKOUT_STARTED_EVENT,
            cart_id: cartId,
            value: total,
            shipping,
            currency,
            products: getProductDetails(lineItems),
        };
        sendTrackEvent(CHECKOUT_STARTED_EVENT, eventData);
    };

    const orderCompletedEvent = (orderNumber, lineItems) => {
        const eventData = {
            ...globalProperties(),
            event_name: ORDER_COMPLETE_EVENT,
            cart_id: cartId,
            order_id: `CM${orderNumber}`,
            products: getProductDetails(lineItems),
        };
        sendTrackEvent(ORDER_COMPLETE_EVENT, eventData);
    };

    const noOp = () => {};

    const events = sendEvents ? {
        productClicked: productClickedEvent,
        productViewed: productViewedEvent,
        productAdded: productAddedEvent,
        productRemoved: productRemovedEvent,
        cartViewed: cartViewedEvent,
        checkoutStarted: checkoutStartedEvent,
        orderCompleted: orderCompletedEvent,
        pageTrack: sendPageEvent,
    } : {
        productClicked: noOp,
        productViewed: noOp,
        productAdded: noOp,
        productRemoved: noOp,
        cartViewed: noOp,
        checkoutStarted: noOp,
        orderCompleted: noOp,
        pageTrack: noOp,
    };

    return <SegmentContext.Provider value={events}>{children}</SegmentContext.Provider>;
};

SegmentProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export const useSegment = () => useContext(SegmentContext);
